import { useEffect, type RefObject } from 'react';
import type { OnboardingQuestion } from '../lib/onboardingProfile';

/** Matches Onboarding step carousel transition duration (seconds → ms). */
export const ONBOARDING_STEP_TRANSITION_MS = 620;

const FOCUSABLE_STEP_TYPES = new Set<OnboardingQuestion['type']>([
  'text',
  'phone',
  'contact-info',
]);

/**
 * Focus the primary input when a typing step becomes active.
 * `autoFocus` alone does not work because all steps mount at once in the vertical carousel.
 */
export function useOnboardingStepFocus(
  rootRef: RefObject<HTMLElement | null>,
  currentStep: number,
  questions: OnboardingQuestion[],
) {
  useEffect(() => {
    const question = questions[currentStep];
    if (!question || !FOCUSABLE_STEP_TYPES.has(question.type)) return;

    const timer = window.setTimeout(() => {
      const root = rootRef.current;
      if (!root) return;

      const stepRoot = root.querySelector('[data-onboarding-active-step="true"]');
      if (!stepRoot) return;

      const target = stepRoot.querySelector<HTMLElement>('[data-onboarding-focus]');
      if (!target || target.matches('select') || target.hasAttribute('disabled')) return;

      try {
        target.focus({ preventScroll: true });
      } catch {
        target.focus();
      }
    }, ONBOARDING_STEP_TRANSITION_MS);

    return () => window.clearTimeout(timer);
  }, [rootRef, currentStep, questions]);
}
